import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Database,
  Edit3,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import {
  sapIntelligenceApi,
  type BuildResponse,
  type ColumnUpdatePayload,
  type ModelOverview,
  type QueryResponse,
  type SapColumn,
} from '../services/sapIntelligenceService';

// ── column_metadata helpers ─────────────────────────────────────────────
// The three rich-metadata fields live at the ROOT of ``column_metadata``
// JSONB. The BE merges edits in (other keys survive); the FE pulls them
// out here for display and seeds the edit draft from the same source.
function metaUnit(col: SapColumn): string {
  const v = (col.column_metadata ?? {})['unit'];
  return typeof v === 'string' ? v : '';
}

function metaUnitSymbol(col: SapColumn): string {
  const v = (col.column_metadata ?? {})['unit_symbol'];
  return typeof v === 'string' ? v : '';
}

function metaSynonyms(col: SapColumn): string[] {
  const v = (col.column_metadata ?? {})['synonyms'];
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === 'string' && x.trim().length > 0);
}

// Comma-separated <-> array conversion for the synonyms text input.
// Split on commas, trim, drop empties. Users type "revenue, top line, sales".
function synonymsFromInput(s: string): string[] {
  return s
    .split(',')
    .map((x) => x.trim())
    .filter((x) => x.length > 0);
}

/**
 * SAP intelligence page.
 *
 * Single page that exercises every endpoint of the new
 * `/api/v1/sap-intel/*` surface:
 *
 *   1. List built models and their column counts
 *   2. Build / refresh a model (calls SAP, runs LLM enrichment, embeds)
 *   3. Inspect + inline-edit a model's columns (auto re-embeds on save)
 *   4. Ask a natural-language question against the model — backend turns it
 *      into an OData call and returns rows + a narrative summary.
 *
 * UI is deliberately plain Tailwind so no extra UI libraries are pulled in.
 */
export default function SapIntelligencePage(): React.ReactElement {
  const [models, setModels] = useState<ModelOverview[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState<string | null>(null);

  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [columns, setColumns] = useState<SapColumn[]>([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [columnsError, setColumnsError] = useState<string | null>(null);
  const [columnFilter, setColumnFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'dimension' | 'measure' | 'parameter'>(
    'all',
  );

  const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<ColumnUpdatePayload>({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // ── Build modal state ────────────────────────────────────────────────
  const [showBuildModal, setShowBuildModal] = useState(false);
  const [buildModelName, setBuildModelName] = useState('');
  const [buildScope, setBuildScope] = useState<'global' | 'user'>('global');
  const [buildDescription, setBuildDescription] = useState('');
  const [buildBusy, setBuildBusy] = useState(false);
  const [buildResult, setBuildResult] = useState<BuildResponse | null>(null);
  const [buildError, setBuildError] = useState<string | null>(null);

  const [refreshingModel, setRefreshingModel] = useState<string | null>(null);
  const [deletingModel, setDeletingModel] = useState<string | null>(null);

  // ── Query box state ──────────────────────────────────────────────────
  const [queryText, setQueryText] = useState('');
  const [queryBusy, setQueryBusy] = useState(false);
  const [queryResult, setQueryResult] = useState<QueryResponse | null>(null);
  const [queryError, setQueryError] = useState<string | null>(null);

  // ─────────────────────────────────────────────────────────────────────
  // Loaders
  // ─────────────────────────────────────────────────────────────────────

  const loadModels = useCallback(async () => {
    setModelsLoading(true);
    setModelsError(null);
    try {
      const list = await sapIntelligenceApi.listModels();
      setModels(list);
      // Auto-select the first model if none selected.
      if (!selectedModel && list.length > 0) {
        setSelectedModel(list[0].model_name);
      }
    } catch (err) {
      setModelsError(humanError(err));
    } finally {
      setModelsLoading(false);
    }
    // selectedModel intentionally omitted from deps — we only auto-select when none is picked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadColumns = useCallback(async (modelName: string) => {
    setColumnsLoading(true);
    setColumnsError(null);
    try {
      const list = await sapIntelligenceApi.listColumns(modelName);
      // Sort: parameters first, then measures, then dimensions, alphabetical inside each group
      list.sort((a, b) => {
        const rank = (r: string | null) =>
          r === 'parameter' ? 0 : r === 'measure' ? 1 : 2;
        const r = rank(a.role) - rank(b.role);
        if (r !== 0) return r;
        return a.column_name.localeCompare(b.column_name);
      });
      setColumns(list);
    } catch (err) {
      setColumnsError(humanError(err));
    } finally {
      setColumnsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  useEffect(() => {
    if (selectedModel) {
      loadColumns(selectedModel);
    } else {
      setColumns([]);
    }
  }, [selectedModel, loadColumns]);

  // ─────────────────────────────────────────────────────────────────────
  // Build / refresh
  // ─────────────────────────────────────────────────────────────────────

  const handleBuild = async () => {
    if (!buildModelName.trim()) {
      setBuildError('Model name is required.');
      return;
    }
    setBuildBusy(true);
    setBuildError(null);
    setBuildResult(null);
    try {
      const result = await sapIntelligenceApi.buildModel(buildModelName.trim(), {
        scope: buildScope,
        model_description: buildDescription.trim() || undefined,
      });
      setBuildResult(result);
      await loadModels();
      setSelectedModel(result.model_name);
      // Auto-load columns for the just-built model so the user immediately
      // sees what landed (or what didn't, if enrichment_warning is set).
      await loadColumns(result.model_name);
      // Auto-close the modal on a clean build (no LLM warning).
      if (!result.enrichment_warning && result.columns_written > 0) {
        setShowBuildModal(false);
      }
    } catch (err) {
      setBuildError(humanError(err));
    } finally {
      setBuildBusy(false);
    }
  };

  const handleRefresh = async (modelName: string) => {
    setRefreshingModel(modelName);
    try {
      await sapIntelligenceApi.refreshModel(modelName, { scope: 'global' });
      await loadModels();
      if (selectedModel === modelName) {
        await loadColumns(modelName);
      }
    } catch (err) {
      setModelsError(humanError(err));
    } finally {
      setRefreshingModel(null);
    }
  };

  const handleDelete = async (modelName: string) => {
    const confirmed = window.confirm(
      `Delete every row for "${modelName}"?\n\n` +
        `This wipes both GLOBAL rows and your user-specific overrides for this ` +
        `model so you can rebuild it from scratch. SAP itself is not touched.`,
    );
    if (!confirmed) return;
    setDeletingModel(modelName);
    try {
      await sapIntelligenceApi.deleteModel(modelName, 'all');
      if (selectedModel === modelName) {
        setSelectedModel(null);
        setColumns([]);
      }
      await loadModels();
    } catch (err) {
      setModelsError(humanError(err));
    } finally {
      setDeletingModel(null);
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // Inline edit
  // ─────────────────────────────────────────────────────────────────────

  const startEdit = (col: SapColumn) => {
    setEditingColumnId(col.id);
    setEditDraft({
      business_name: col.business_name ?? '',
      business_definition: col.business_definition ?? '',
      description: col.description ?? '',
      usage_suggestions: col.usage_suggestions ?? '',
      // The three new fields live inside ``column_metadata`` JSONB
      // — extract them via the helpers so the edit form is seeded
      // with whatever the LLM (or a prior user edit) wrote.
      unit: metaUnit(col),
      unit_symbol: metaUnitSymbol(col),
      synonyms: metaSynonyms(col),
    });
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingColumnId(null);
    setEditDraft({});
    setEditError(null);
  };

  const saveEdit = async () => {
    if (!editingColumnId) return;
    setEditSaving(true);
    setEditError(null);
    try {
      // Only send fields that actually changed (i.e. were touched in the form).
      const payload: ColumnUpdatePayload = {};
      (
        [
          'business_name',
          'business_definition',
          'description',
          'usage_suggestions',
          'unit',
          'unit_symbol',
        ] as const
      ).forEach((k) => {
        const v = editDraft[k];
        if (v !== undefined) payload[k] = v;
      });
      // ``synonyms`` is the only list-typed editable field — copy it
      // through verbatim when present (empty array clears the list).
      if (editDraft.synonyms !== undefined) {
        payload.synonyms = editDraft.synonyms;
      }
      const updated = await sapIntelligenceApi.updateColumn(editingColumnId, payload);
      setColumns((prev) =>
        prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)),
      );
      cancelEdit();
    } catch (err) {
      setEditError(humanError(err));
    } finally {
      setEditSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // NL query
  // ─────────────────────────────────────────────────────────────────────

  const handleQuery = async () => {
    if (!selectedModel || !queryText.trim()) return;
    setQueryBusy(true);
    setQueryError(null);
    setQueryResult(null);
    try {
      const r = await sapIntelligenceApi.query(selectedModel, queryText.trim());
      setQueryResult(r);
    } catch (err) {
      setQueryError(humanError(err));
    } finally {
      setQueryBusy(false);
    }
  };

  const filteredColumns = useMemo(() => {
    const needle = columnFilter.trim().toLowerCase();
    return columns.filter((c) => {
      if (roleFilter !== 'all' && (c.role ?? '').toLowerCase() !== roleFilter) return false;
      if (!needle) return true;
      const blob = [
        c.column_name,
        c.business_name,
        c.business_definition,
        c.description,
        c.usage_suggestions,
        c.sap_label,
        // Make the three new JSONB-root fields searchable too — typing
        // "USD" or "case volume" should hit columns whose unit / symbol
        // / synonyms match even if the technical name doesn't.
        metaUnit(c),
        metaUnitSymbol(c),
        metaSynonyms(c).join(' '),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(needle);
    });
  }, [columns, columnFilter, roleFilter]);

  return (
    <div className="relative flex h-full w-full flex-col overflow-auto bg-[#FAF7F1] text-[#1F1A20] p-6">
      <div className="mx-auto w-full max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold text-[#1F1A20] flex items-center gap-2">
              <Database className="w-6 h-6 text-[#6E3450]" />
              SAP Intelligence
            </h1>
            <p className="text-sm text-[#7B6B72] mt-1">
              Build, curate, and query SAP Datasphere analytical models with LLM-generated
              column descriptions + semantic retrieval.
            </p>
          </div>
          <button
            onClick={() => {
              setBuildModelName('');
              setBuildScope('global');
              setBuildDescription('');
              setBuildResult(null);
              setBuildError(null);
              setShowBuildModal(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-[#6E3450] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#5A2A41] transition-colors"
          >
            <Plus className="w-4 h-4" /> Build model
          </button>
        </div>

        {/* Models row */}
        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700">Built models</h2>
            <button
              onClick={loadModels}
              disabled={modelsLoading}
              className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
            >
              {modelsLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              Reload
            </button>
          </div>
          {modelsError && (
            <ErrorBanner message={modelsError} onDismiss={() => setModelsError(null)} />
          )}
          {models.length === 0 && !modelsLoading && !modelsError ? (
            <EmptyState
              icon={<Database className="w-8 h-8 text-slate-400" />}
              title="No models built yet"
              hint="Click “Build model” above to fetch metadata for an SAP analytical model."
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {models.map((m) => {
                const active = selectedModel === m.model_name;
                return (
                  <div
                    key={m.model_name}
                    onClick={() => setSelectedModel(m.model_name)}
                    className={`cursor-pointer rounded-lg border p-3 transition ${
                      active
                        ? 'border-[#6E3450] bg-[#F5E9EE]/50 ring-1 ring-[#6E3450]'
                        : 'border-slate-200 bg-white hover:border-slate-400'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-sm font-semibold text-slate-900 truncate">
                          {m.model_name}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {m.column_count} columns · {m.dimension_count} dim ·{' '}
                          {m.measure_count} meas · {m.parameter_count} param
                        </div>
                        {m.user_modified_count > 0 && (
                          <div className="text-xs text-amber-600 mt-1">
                            {m.user_modified_count} user-edited
                          </div>
                        )}
                        <div className="text-xs text-slate-400 mt-1">
                          {m.last_built_at ? `Updated ${formatTime(m.last_built_at)}` : '—'}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRefresh(m.model_name);
                          }}
                          disabled={
                            refreshingModel === m.model_name ||
                            deletingModel === m.model_name
                          }
                          title="Refresh from SAP (preserves user edits)"
                          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
                        >
                          {refreshingModel === m.model_name ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(m.model_name);
                          }}
                          disabled={
                            deletingModel === m.model_name ||
                            refreshingModel === m.model_name
                          }
                          title="Delete every row for this model so it can be rebuilt from scratch"
                          className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                        >
                          {deletingModel === m.model_name ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Columns table */}
        {selectedModel && (
          <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 p-4 flex flex-wrap items-center gap-3 justify-between">
              <div>
                <h2 className="text-sm font-semibold text-slate-700">Columns</h2>
                <div className="text-xs text-slate-500 font-mono">{selectedModel}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                  <input
                    value={columnFilter}
                    onChange={(e) => setColumnFilter(e.target.value)}
                    placeholder="Filter columns…"
                    className="rounded-md border border-slate-300 pl-8 pr-3 py-1.5 text-sm focus:border-[#6E3450] focus:outline-none"
                  />
                </div>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value as typeof roleFilter)}
                  className="rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-[#6E3450] focus:outline-none"
                >
                  <option value="all">All roles</option>
                  <option value="parameter">Parameters</option>
                  <option value="measure">Measures</option>
                  <option value="dimension">Dimensions</option>
                </select>
              </div>
            </div>

            {columnsError && (
              <div className="p-4">
                <ErrorBanner message={columnsError} onDismiss={() => setColumnsError(null)} />
              </div>
            )}

            {columnsLoading ? (
              <div className="p-12 flex items-center justify-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading columns…
              </div>
            ) : filteredColumns.length === 0 ? (
              <EmptyState
                icon={<Database className="w-8 h-8 text-slate-400" />}
                title="No columns match"
                hint={
                  columns.length === 0
                    ? 'Build this model to fetch its $metadata and generate descriptions.'
                    : 'Try clearing the filter.'
                }
              />
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredColumns.map((col) => (
                  <ColumnRow
                    key={col.id}
                    col={col}
                    isEditing={editingColumnId === col.id}
                    editDraft={editDraft}
                    setEditDraft={setEditDraft}
                    onEdit={() => startEdit(col)}
                    onCancel={cancelEdit}
                    onSave={saveEdit}
                    saving={editSaving}
                    error={editingColumnId === col.id ? editError : null}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Ask a question */}
        {selectedModel && (
          <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[#6E3450]" />
              <h2 className="text-sm font-semibold text-slate-700">Ask a question</h2>
              <span className="text-xs text-slate-500 font-mono">{selectedModel}</span>
            </div>
            <div className="flex gap-2">
              <input
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !queryBusy) handleQuery();
                }}
                placeholder="e.g. YTD plan revenue by brand"
                className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#6E3450] focus:outline-none"
              />
              <button
                onClick={handleQuery}
                disabled={queryBusy || !queryText.trim()}
                className="inline-flex items-center gap-2 rounded-md bg-[#6E3450] px-4 py-2 text-sm font-medium text-white hover:bg-[#5A2A41] disabled:opacity-50"
              >
                {queryBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ask'}
              </button>
            </div>
            {queryError && (
              <div className="mt-3">
                <ErrorBanner message={queryError} onDismiss={() => setQueryError(null)} />
              </div>
            )}
            {queryResult && (
              <QueryResultPanel result={queryResult} schemaColumns={columns} />
            )}
          </section>
        )}
      </div>

      {/* Build modal */}
      {showBuildModal && (
        <BuildModal
          modelName={buildModelName}
          setModelName={setBuildModelName}
          scope={buildScope}
          setScope={setBuildScope}
          description={buildDescription}
          setDescription={setBuildDescription}
          busy={buildBusy}
          result={buildResult}
          error={buildError}
          onClose={() => setShowBuildModal(false)}
          onSubmit={handleBuild}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────

interface ColumnRowProps {
  col: SapColumn;
  isEditing: boolean;
  editDraft: ColumnUpdatePayload;
  setEditDraft: React.Dispatch<React.SetStateAction<ColumnUpdatePayload>>;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
}

function ColumnRow({
  col,
  isEditing,
  editDraft,
  setEditDraft,
  onEdit,
  onCancel,
  onSave,
  saving,
  error,
}: ColumnRowProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const open = expanded || isEditing;

  // ``synonyms`` is the only list-typed editable field — set it
  // separately so the text-input/CSV-parser path is type-safe and
  // ``setField`` stays focused on plain string columns.
  const setField = (
    k: 'business_name' | 'business_definition' | 'description' | 'usage_suggestions' | 'unit' | 'unit_symbol',
    v: string,
  ) => setEditDraft((prev) => ({ ...prev, [k]: v }));

  const setSynonyms = (csv: string) =>
    setEditDraft((prev) => ({ ...prev, synonyms: synonymsFromInput(csv) }));

  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-3">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 text-slate-400 hover:text-slate-700"
        >
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-semibold text-slate-900 truncate">
              {col.column_name}
            </span>
            <RoleBadge role={col.role} />
            {col.data_type && (
              <span className="text-xs text-slate-500 font-mono">{col.data_type}</span>
            )}
            {/* Unit badge — surface ``unit``/``unit_symbol`` from        */}
            {/* ``column_metadata`` JSONB right in the collapsed header.   */}
            {/* Without this you'd have to expand or click edit to see the */}
            {/* values, which defeats the point of having them on the row. */}
            {(() => {
              const u = metaUnit(col);
              const s = metaUnitSymbol(col);
              if (!u && !s) return null;
              const label = u && s ? `${u} (${s})` : u || s;
              return (
                <span
                  className="rounded bg-sky-100 text-sky-800 text-xs px-1.5 py-0.5 font-mono"
                  title="Unit / symbol (from column_metadata)"
                >
                  {label}
                </span>
              );
            })()}
            {col.is_user_modified && (
              <span className="rounded bg-amber-100 text-amber-800 text-xs px-1.5 py-0.5">
                edited
              </span>
            )}
          </div>
          <div className="text-sm text-slate-700 mt-0.5">
            {col.business_name || col.sap_label || (
              <span className="text-slate-400 italic">No business name yet</span>
            )}
          </div>
          {!open && col.business_definition && (
            <div className="text-xs text-slate-500 mt-1 line-clamp-1">
              {col.business_definition}
            </div>
          )}
        </div>
        {!isEditing && (
          <button
            onClick={onEdit}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            title="Edit"
          >
            <Edit3 className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3 ml-7 space-y-3">
          {isEditing ? (
            <>
              <EditField
                label="Business name"
                value={editDraft.business_name ?? ''}
                onChange={(v) => setField('business_name', v)}
                placeholder="Short, human-friendly title"
                rows={1}
              />
              <EditField
                label="Business definition"
                value={editDraft.business_definition ?? ''}
                onChange={(v) => setField('business_definition', v)}
                placeholder="One sentence defining the value in business terms"
                rows={2}
              />
              <EditField
                label="Description"
                value={editDraft.description ?? ''}
                onChange={(v) => setField('description', v)}
                placeholder="Detailed description (units, conventions, quirks)"
                rows={4}
              />
              <EditField
                label="Usage suggestions"
                value={editDraft.usage_suggestions ?? ''}
                onChange={(v) => setField('usage_suggestions', v)}
                placeholder="Which questions does this column answer? Common partners?"
                rows={2}
              />
              {/* The three column_metadata-root fields. Unit + symbol */}
              {/* sit on one row to mirror how they appear in chart      */}
              {/* labels (e.g. "USD ($)"). Synonyms is a CSV input that  */}
              {/* parses to a list on save.                              */}
              <div className="grid grid-cols-2 gap-3">
                <EditField
                  label="Unit"
                  value={editDraft.unit ?? ''}
                  onChange={(v) => setField('unit', v)}
                  placeholder='e.g. USD, %, cases, pounds'
                  rows={1}
                />
                <EditField
                  label="Unit symbol"
                  value={editDraft.unit_symbol ?? ''}
                  onChange={(v) => setField('unit_symbol', v)}
                  placeholder='e.g. $, %, kg'
                  rows={1}
                />
              </div>
              <EditField
                label="Synonyms (comma-separated)"
                value={(editDraft.synonyms ?? []).join(', ')}
                onChange={(v) => setSynonyms(v)}
                placeholder='e.g. revenue, top line, gross sales'
                rows={1}
              />
              {error && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                  {error}
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={onSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-md bg-[#6E3450] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#5A2A41] disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </button>
                <button
                  onClick={onCancel}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <span className="text-xs text-slate-500">
                  Saving will mark this row as user-edited and re-embed it.
                </span>
              </div>
            </>
          ) : (
            <>
              <DetailField label="Business definition" value={col.business_definition} />
              <DetailField label="Description" value={col.description} />
              <DetailField label="Usage suggestions" value={col.usage_suggestions} />
              {/* Read-only mirror of the column_metadata-root fields. */}
              {/* Always render Unit + Synonyms so analysts can see they */}
              {/* exist as editable fields even when empty — without     */}
              {/* this they only appear after clicking Edit, which is    */}
              {/* the bug the user reported. ``MetaField`` shows ``—``   */}
              {/* (em-dash) for empty values to match the placeholder    */}
              {/* convention used elsewhere on the page.                  */}
              {(() => {
                const u = metaUnit(col);
                const s = metaUnitSymbol(col);
                const display = u && s ? `${u} (${s})` : u || s || '';
                return <MetaField label="Unit" value={display} />;
              })()}
              <MetaField
                label="Synonyms"
                value={metaSynonyms(col).join(', ')}
              />
              {col.sap_label && col.sap_label !== col.column_name && (
                <DetailField label="SAP label" value={col.sap_label} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Like ``DetailField`` but renders an em-dash placeholder for empty
 * values instead of collapsing the row. Used for ``column_metadata``-
 * root fields (unit, synonyms) so the analyst can see at a glance
 * that the field exists and is editable even when no value has been
 * set yet. ``DetailField`` is kept for the LLM-prose fields where a
 * blank value really does mean "this section doesn't apply".
 */
function MetaField({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.ReactElement {
  const isEmpty = !value.trim();
  return (
    <div>
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div
        className={
          'text-sm whitespace-pre-wrap ' +
          (isEmpty ? 'text-slate-400 italic' : 'text-slate-700')
        }
      >
        {isEmpty ? '—' : value}
      </div>
    </div>
  );
}

function RoleBadge({ role }: { role: string | null }): React.ReactElement {
  const r = (role ?? '').toLowerCase();
  const cls =
    r === 'measure'
      ? 'bg-emerald-100 text-emerald-800'
      : r === 'parameter'
        ? 'bg-purple-100 text-purple-800'
        : r === 'dimension'
          ? 'bg-[#F5E9EE] text-[#5A2A41]'
          : 'bg-slate-100 text-slate-700';
  return (
    <span className={`rounded text-xs px-1.5 py-0.5 ${cls}`}>{role || 'unknown'}</span>
  );
}

function EditField({
  label,
  value,
  onChange,
  placeholder,
  rows,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows: number;
}): React.ReactElement {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#6E3450] focus:outline-none"
      />
    </div>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}): React.ReactElement | null {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="text-sm text-slate-700 whitespace-pre-wrap">{value}</div>
    </div>
  );
}

interface BuildModalProps {
  modelName: string;
  setModelName: (v: string) => void;
  scope: 'global' | 'user';
  setScope: (v: 'global' | 'user') => void;
  description: string;
  setDescription: (v: string) => void;
  busy: boolean;
  result: BuildResponse | null;
  error: string | null;
  onClose: () => void;
  onSubmit: () => void;
}

function BuildModal({
  modelName,
  setModelName,
  scope,
  setScope,
  description,
  setDescription,
  busy,
  result,
  error,
  onClose,
  onSubmit,
}: BuildModalProps): React.ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-base font-semibold">Build SAP analytical model</h3>
          <button
            onClick={onClose}
            className="rounded p-1 text-slate-500 hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-600">Model name</label>
            <input
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              placeholder="e.g. 5CO_AM_COPA_SUMMARY_ODATA"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:border-[#6E3450] focus:outline-none"
              disabled={busy}
            />
            <p className="mt-1 text-xs text-slate-500">
              Must match a view name visible in the SAP catalog for the authenticated user.
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Scope</label>
            <div className="mt-1 flex gap-3">
              <ScopePill
                active={scope === 'global'}
                onClick={() => setScope('global')}
                title="Global (all users)"
                hint="user_id NULL — visible to every user"
              />
              <ScopePill
                active={scope === 'user'}
                onClick={() => setScope('user')}
                title="Just me"
                hint="Stored under your user_id only"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Context shared with the LLM during enrichment"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#6E3450] focus:outline-none"
              disabled={busy}
            />
          </div>

          {error && <ErrorBanner message={error} />}

          {result && (
            <div
              className={`rounded-md border p-3 text-sm ${
                result.enrichment_warning
                  ? 'border-amber-300 bg-amber-50'
                  : 'border-emerald-200 bg-emerald-50'
              }`}
            >
              <div
                className={`font-semibold ${
                  result.enrichment_warning ? 'text-amber-900' : 'text-emerald-900'
                }`}
              >
                Built {result.columns_written} column
                {result.columns_written === 1 ? '' : 's'}
                {result.columns_skipped ? ` (${result.columns_skipped} skipped)` : ''}.
              </div>
              <div className="text-xs mt-1 space-y-0.5">
                <div>
                  Enriched (LLM): {result.enriched_count ?? 0} ·
                  {' '}Embedded: {result.embedded_count ?? 0}
                </div>
                {result.space_id && (
                  <div className="font-mono">space_id: {result.space_id}</div>
                )}
              </div>
              {result.enrichment_warning && (
                <div className="mt-2 text-xs text-amber-900 whitespace-pre-wrap">
                  ⚠ {result.enrichment_warning}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
          <button
            onClick={onSubmit}
            disabled={busy || !modelName.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-[#6E3450] px-4 py-2 text-sm font-medium text-white hover:bg-[#5A2A41] disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {busy ? 'Building…' : 'Build'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScopePill({
  active,
  onClick,
  title,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  hint: string;
}): React.ReactElement {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-md border p-2 text-left transition ${
        active
          ? 'border-[#6E3450] bg-[#F5E9EE] ring-1 ring-[#6E3450]'
          : 'border-slate-200 hover:border-slate-400'
      }`}
    >
      <div className="text-sm font-medium text-slate-900">{title}</div>
      <div className="text-xs text-slate-500">{hint}</div>
    </button>
  );
}

function QueryResultPanel({
  result,
  schemaColumns,
}: {
  result: QueryResponse;
  schemaColumns: SapColumn[];
}): React.ReactElement {
  // Build a name → role lookup so we can split fetch_plan.columns into
  // dimensions / measures / parameters without needing the backend to
  // pre-classify the response.
  const roleByName = useMemo(() => {
    const map = new Map<string, string>();
    schemaColumns.forEach((c) => {
      map.set(c.column_name, (c.role || '').toLowerCase());
    });
    return map;
  }, [schemaColumns]);

  if (result.error) {
    return (
      <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        {result.error}
      </div>
    );
  }

  const dimensions: string[] = [];
  const measures: string[] = [];
  const parameters: string[] = [];
  const others: string[] = [];
  result.fetch_plan.columns.forEach((name) => {
    const role = roleByName.get(name);
    if (role === 'measure') measures.push(name);
    else if (role === 'dimension') dimensions.push(name);
    else if (role === 'parameter') parameters.push(name);
    else others.push(name);
  });

  const filterLines = result.fetch_plan.filters
    .map((f) => formatFilter(f))
    .filter((s): s is string => Boolean(s));
  const inputParamLines = Object.entries(result.fetch_plan.input_parameters).map(
    ([k, v]) => `${k} = ${String(v)}`,
  );

  return (
    <div className="mt-4 rounded-md border border-slate-200 bg-white p-4 text-sm space-y-3">
      <div className="text-slate-900 font-medium">{result.query}</div>

      <PlanGroup label="Dimensions" items={dimensions} emptyHint="—" />
      <PlanGroup label="Measures" items={measures} emptyHint="—" />
      {parameters.length > 0 && (
        <PlanGroup label="Parameters" items={parameters} emptyHint="—" />
      )}
      {others.length > 0 && (
        <PlanGroup label="Other columns" items={others} emptyHint="—" />
      )}

      <div>
        <span className="text-slate-500 font-semibold">Filters: </span>
        {filterLines.length === 0 && inputParamLines.length === 0 ? (
          <span className="text-slate-400">none</span>
        ) : (
          <span className="font-mono text-slate-800">
            {[...filterLines, ...inputParamLines].join('; ')}
          </span>
        )}
      </div>

      {result.fetch_plan.period && (
        <div className="text-xs text-slate-500">
          Period: <span className="font-mono">{result.fetch_plan.period}</span>
          {result.fetch_plan.fiscal_start_value !== null &&
            result.fetch_plan.fiscal_end_value !== null && (
              <>
                {' '}
                · Fiscal range:{' '}
                <span className="font-mono">
                  {result.fetch_plan.fiscal_start_value} →{' '}
                  {result.fetch_plan.fiscal_end_value}
                </span>
              </>
            )}
        </div>
      )}

      <div className="text-xs text-slate-400 italic">
        Plan only — no SAP OData call was made.
      </div>
    </div>
  );
}

function PlanGroup({
  label,
  items,
  emptyHint,
}: {
  label: string;
  items: string[];
  emptyHint: string;
}): React.ReactElement {
  return (
    <div>
      <span className="text-slate-500 font-semibold">{label}: </span>
      {items.length === 0 ? (
        <span className="text-slate-400">{emptyHint}</span>
      ) : (
        <span className="font-mono text-slate-800">{items.join(', ')}</span>
      )}
    </div>
  );
}

function formatFilter(f: Record<string, unknown>): string | null {
  const column = typeof f.column === 'string' ? f.column : '';
  const operator = typeof f.operator === 'string' ? f.operator : 'eq';
  const value = f.value;
  if (column && value !== undefined && value !== null) {
    if (operator === 'eq') return `${column}=${String(value)}`;
    return `${column} ${operator} ${String(value)}`;
  }
  if (typeof f.odata_syntax === 'string') return f.odata_syntax;
  return null;
}

function ErrorBanner({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss?: () => void;
}): React.ReactElement {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
      <span className="flex-1">{message}</span>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="text-red-700 hover:text-red-900"
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function EmptyState({
  icon,
  title,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  hint: string;
}): React.ReactElement {
  return (
    <div className="p-10 flex flex-col items-center text-center text-slate-500">
      {icon}
      <div className="mt-2 font-medium text-slate-700">{title}</div>
      <div className="mt-1 text-xs">{hint}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

function humanError(err: unknown): string {
  const e = err as { response?: { data?: { detail?: string; message?: string } }; message?: string };
  return (
    e?.response?.data?.detail ||
    e?.response?.data?.message ||
    e?.message ||
    'Unknown error'
  );
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

